import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'
import PropTypes from 'prop-types'
import { loadFacilities } from '../utils/dataLoader'
import { aggregateLocationData } from '../utils/narrativeDataUtils'

const NarrativeContext = createContext(null)

/**
 * Narrative Context Provider
 *
 * Manages facility data loading (primary), narrative loading (optional), and location context state
 * Data-first approach: facility data always loads, narratives enhance when available
 */
export function NarrativeProvider({ children }) {
  const [currentLocationId, setCurrentLocationId] = useState(null)
  const [currentLocation, setCurrentLocation] = useState(null)
  const [activeCategory, setActiveCategory] = useState('health') // NEW: Track active category
  const [facilityData, setFacilityData] = useState(null) // Primary data
  const [narrativeData, setNarrativeData] = useState(null) // Optional enhancement
  const [loading, setLoading] = useState(false)
  const [locationsData, setLocationsData] = useState(null)
  const [allFacilities, setAllFacilities] = useState({
    health: [],
    education: [],
  }) // NEW: Multi-category facilities

  // Cache for loaded narratives (facility data calculated on demand, no cache needed)
  const [narrativeCache, setNarrativeCache] = useState({})

  // Load locations and facilities data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load locations
        const locationsResponse = await fetch('/data/locations.json')
        if (!locationsResponse.ok)
          throw new Error('Failed to load locations data')
        const locations = await locationsResponse.json()
        setLocationsData(locations)

        // Load both health and education facilities
        const healthFacilities = await loadFacilities('health')
        const educationFacilities = await loadFacilities('education')

        setAllFacilities({
          health: healthFacilities,
          education: educationFacilities,
        })
      } catch (err) {
        console.error('Error loading data:', err)
        // Don't block on errors - let components handle missing data
      }
    }

    loadData()
  }, [])

  // Find location by ID in hierarchical structure
  const findLocationById = useCallback(
    locationId => {
      if (!locationsData) return null

      for (const district of locationsData.districts) {
        if (district.id === locationId) {
          return {
            ...district,
            type: 'district',
            parent: null,
            children: district.subcounties,
          }
        }

        if (district.subcounties) {
          for (const subcounty of district.subcounties) {
            if (subcounty.id === locationId) {
              return {
                ...subcounty,
                type: 'subcounty',
                parent: district,
                children: subcounty.parishes || null,
              }
            }

            if (subcounty.parishes) {
              for (const parish of subcounty.parishes) {
                if (parish.id === locationId) {
                  return {
                    ...parish,
                    type: 'parish',
                    parent: subcounty,
                    children: null,
                  }
                }
              }
            }
          }
        }
      }
      return null
    },
    [locationsData]
  )

  // Load facility data for a specific location (PRIMARY - always runs)
  const loadFacilityData = useCallback(
    async (locationId, category = 'health') => {
      if (!locationsData || allFacilities[category].length === 0) {
        return null
      }

      try {
        const aggregatedData = aggregateLocationData(
          allFacilities[category],
          locationId,
          locationsData,
          'parish', // max depth, TODO: get from config
          category // Pass category for appropriate aggregation
        )

        setFacilityData(aggregatedData)
        return aggregatedData
      } catch (err) {
        console.error('Error aggregating facility data:', err)
        setFacilityData(null)
        return null
      }
    },
    [locationsData, allFacilities]
  )

  // Load narrative for a specific location and story (OPTIONAL - non-blocking)
  const loadNarrative = useCallback(
    async (locationId, storyId = 'healthcare-access') => {
      // Check cache first
      const cacheKey = `${locationId}-${storyId}`
      if (narrativeCache[cacheKey]) {
        // eslint-disable-next-line no-console
        console.log(`Loading narrative from cache: ${cacheKey}`)
        setNarrativeData(narrativeCache[cacheKey])
        return narrativeCache[cacheKey]
      }

      try {
        const response = await fetch(
          `/data/narratives/${locationId}/${storyId}.json`
        )

        if (!response.ok) {
          // Silently fail - narratives are optional
          // eslint-disable-next-line no-console
          console.log(`No narrative available for ${locationId}/${storyId}`)
          setNarrativeData(null)
          return null
        }

        // Check content type before parsing
        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          // eslint-disable-next-line no-console
          console.log('Invalid content type for narrative')
          setNarrativeData(null)
          return null
        }

        let data
        try {
          data = await response.json()
          // eslint-disable-next-line no-unused-vars
        } catch (parseError) {
          // eslint-disable-next-line no-console
          console.log('Failed to parse narrative JSON')
          setNarrativeData(null)
          return null
        }

        // Cache the narrative
        setNarrativeCache(prev => ({
          ...prev,
          [cacheKey]: data,
        }))

        setNarrativeData(data)
        return data
      } catch (err) {
        // Silently fail - narratives are optional, don't set error state
        // eslint-disable-next-line no-console
        console.log('Narrative not available:', err.message)
        setNarrativeData(null)
        return null
      }
    },
    [narrativeCache]
  )

  // Change location context (loads facility data + optional narrative)
  const changeLocation = useCallback(
    async (locationId, storyId = 'healthcare-access', category = 'health') => {
      setLoading(true)

      const location = findLocationById(locationId)

      if (!location) {
        console.error(`Location not found: ${locationId}`)
        setLoading(false)
        return
      }

      setCurrentLocationId(locationId)
      setCurrentLocation(location)
      setActiveCategory(category)

      // Load facility data (PRIMARY - always runs)
      await loadFacilityData(locationId, category)

      // Load narrative (OPTIONAL - non-blocking, runs in background)
      loadNarrative(locationId, storyId) // Don't await - let it run in background

      setLoading(false)
    },
    [findLocationById, loadFacilityData, loadNarrative]
  )

  // Initialize with default location (Kayunga)
  useEffect(() => {
    if (locationsData && !currentLocationId) {
      changeLocation('d-kayunga')
    }
  }, [locationsData, currentLocationId, changeLocation])

  const value = {
    currentLocationId,
    currentLocation,
    activeCategory,
    facilityData, // PRIMARY - facility data with metrics
    narrativeData, // OPTIONAL - AI narratives when available
    loading,
    locationsData,
    allFacilities,
    changeLocation,
    loadFacilityData,
    loadNarrative,
    findLocationById,
    setActiveCategory,
  }

  return (
    <NarrativeContext.Provider value={value}>
      {children}
    </NarrativeContext.Provider>
  )
}

NarrativeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * Hook to use narrative context
 */
export function useNarrative() {
  const context = useContext(NarrativeContext)
  if (!context) {
    throw new Error('useNarrative must be used within a NarrativeProvider')
  }
  return context
}

export default NarrativeContext
